import type { ReactNode } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Group,
} from '@optiaxiom/react';

interface AppDialogProps {
  open: boolean;
  /** Called on any dismissal — overlay click, Esc, the header close (×). */
  onClose: () => void;
  /** Title text. Rendered large/bold (Axiom's DialogHeader otherwise fixes the size). */
  title: ReactNode;
  /** Optional icon shown before the title. */
  titleIcon?: ReactNode;
  /** Optional secondary line under the title. */
  description?: ReactNode;
  /** Footer actions (usually buttons). The footer + its divider render only when set. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'fullscreen';
  /** Escape hatch for a custom content width (e.g. a max-width between sm and md). */
  contentClassName?: string;
  children: ReactNode;
}

/**
 * House-style dialog wrapper around Axiom's `Dialog`. Owns the shared chrome —
 * controlled open/close, header/footer dividers, and the large/bold title (the
 * size has to be set on a child of DialogHeader, not the header itself) — so
 * individual dialogs only provide their title, body, and actions.
 */
export function AppDialog({
  open,
  onClose,
  title,
  titleIcon,
  description,
  footer,
  size = 'md',
  contentClassName,
  children,
}: AppDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size={size} className={contentClassName}>
        <DialogHeader borderB="1" borderColor="border.default" description={description}>
          <Group alignItems="center" gap="12" fontSize="3xl" fontWeight="700">
            {titleIcon}
            {title}
          </Group>
        </DialogHeader>
        <DialogBody>{children}</DialogBody>
        {footer && (
          <DialogFooter borderT="1" borderColor="border.default">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
